import { SpanControl } from "@/Common/Components/SpanControl";
import { CommonTableHeaderComponent } from "@/Common/Components/Tables/CommonTableHeaderComponent";
import { MyleTable } from "@/Common/Components/Tables/MyleTable";
import { MyleTableColumn, MyleTableColumnOptions } from "@/Common/Components/Tables/MyleTableColumn";
import { MyleTableColumnOrderControlv2 } from "@/Common/Components/Tables/MyleTableColumnOrderControlv2";
import { SimplePagerControl } from "@/Common/Components/Tables/SimplePagerControl";
import { MyleHelper } from "@/Common/Helpers/MyleHelper";
import { OpenAttachmentsPopupItem } from "@/Common/Models/Attachments/OpenAttachmentsPopupItem";
import { Person } from "@/Common/Models/Person/Person";
import { PortfolioExamRequestSortOrderType, PortfolioInboxes, RedirectTo, SortDirection } from "@/Common/Models/UI/Enums";
import { ColumnConfigurationModel } from "@/Common/Models/UI/Tables/ColumnConfigurationModel";
import { ITableRenderOptions } from "@/Common/Models/UI/Tables/ITableRenderOptions";
import { MyleTableTarget } from "@/Common/Models/UI/Tables/MyleTableTarget";
import * as LocalOperations from "@/Common/Operations/operations.local";
import * as ServerOperations from "@/Common/Operations/operations.server";
import { LocalEvent } from "@/Common/Stores/Actions/LocalEvent";
import { RouterHistory } from "@/Common/Utilities/Router/RouterHistory";
import { ExportableFile } from "@/ExportableFile/Models/ExportableFile";
import { ApssClient } from "@/Patient/Api/Apss/ApssClient";
import { ApssSettingsContainer } from "@/Portfolio/Components/ApssSettingsContainer";
import { MyAPSSFormTableItem } from "@/Portfolio/Models/MyNotesInboxTableItem";
import { IAPSSInboxModel } from "@/Portfolio/Models/UI/IAPSSInboxModel";
import { PortfolioSaveOptionKey } from "./PortfolioSaveOptionKey";

interface IAPSSInboxProps {
    Model: IAPSSInboxModel;
    Type: PortfolioInboxes;
}

class ApssInbox extends React.Component<IAPSSInboxProps, never> {
    private helper: MyleHelper = sessionStore.helper;

    public componentDidMount(): void {
        $(window).trigger("resize");

        sessionStore.dispatch(LocalOperations.Portfolio.APSS_Reload, { inboxType: this.props.Type });
    }

    public componentDidUpdate(prevProps: IAPSSInboxProps): void {
        $(window).trigger("resize");

        if (prevProps.Type != this.props.Type)
            sessionStore.dispatch(LocalOperations.Portfolio.APSS_Reload, { inboxType: this.props.Type });
    }

    public render(): JSX.Element {
        const saveOptionKey = PortfolioSaveOptionKey.ApssRecent;
        const columnsOptions = this.helper.loadTableColumnOptionsFromSettings(saveOptionKey, this.getMyleTableColumnsDefaultOptions());
        const tableColumns = this.getTableColumns(columnsOptions);
        const tableRenderOptions = this.loadTableRenderOptions(columnsOptions);
        const cfgm: ColumnConfigurationModel[] = this.helper.castColumnConfigurationToMyleTableColumnOptions(columnsOptions.toArray());

        const subInboxTypeLabel = cacheStore.GetString("RequestsLabel");

        return (
            <div className="workspace" onClick={this.saveAndCloseSettingPopup.bind(this)}>
                <CommonTableHeaderComponent
                    title={`${cacheStore.GetString("APSSFORMLABEL")} - ${subInboxTypeLabel}`}
                    searchText={this.props.Model.get("GlobalPortfolioSearchText")}
                    searchPlaceholder={cacheStore.GetString("SearchField")}
                    onSearch={text => sessionStore.dispatch(LocalOperations.Portfolio.APSS_SearchChange, { value: text })}
                    onRefreshClick={() => sessionStore.dispatch(LocalOperations.Portfolio.APSS_Reload, { inboxType: this.props.Type })}
                    onSettingsClick={() => sessionStore.dispatch(LocalOperations.Portfolio.APSS_ToggleOpenCloseSettings)}
                />
                <div id="posref" className="ws-scrollable">
                    <div className="myle-doc-tb main-content table-wrapper">
                        {this.props.Model.get("ShowSettings")
                            && (
                                <ApssSettingsContainer
                                    className="filter-component"
                                    model={this.props.Model}
                                    Type={this.props.Type}
                                    style={{ width: "650px" }}
                                    onClick={(e: React.MouseEvent) => { e.stopPropagation(); }}
                                    onCloseCallback={() => this.saveAndCloseSettingPopup()}
                                    reloadOpetation={LocalOperations.Portfolio.APSS_Reload}
                                    isFromConseilNumerique={false}
                                />
                            )
                        }
                        <MyleTable
                            TableRenderOptions={tableRenderOptions}
                            TableId="RequestsInbox_main_tb"
                            Columns={tableColumns}
                            DataSource={this.props.Model.get("TableData")}
                            ColumnsPreferencesChangedOperation={ServerOperations.Server.Myle_UpdateUserSettings}
                            RowClickOperation={LocalOperations.Portfolio.APSS_RowClick}
                            ColumnSortTarget={MyleTableTarget.Unassigned}
                            SaveOptionskeyTarget={saveOptionKey}
                        />
                    </div>
                </div>
                <div className="ws-footer table-pager-footer">
                    <div className="row">
                        <div className="col-lg-10 col-md-9 col-sm-8">
                            <SimplePagerControl
                                PageSize={this.props.Model.get("SelectedPageSizeOption")}
                                TotalRows={this.props.Model.get("TotalQueryRows")}
                                CurrentPage={this.props.Model.get("CurrentPage")}
                                EventToDispatchOnPageChange={LocalOperations.Portfolio.APSS_PageEvent}
                            />
                        </div>
                    </div>
                </div>

                {this.props.Model.get("ColumnOrderingPopupOpen") && (
                    <MyleTableColumnOrderControlv2
                        TableColumns={cfgm}
                        SaveOptionskeyTarget={saveOptionKey}
                        ContainerId="columnSettingsPopup"
                        CloseColumOrder={LocalOperations.Portfolio.APSS_SearchSettings_HideColumnOrdering}
                    />
                )}
            </div>
        );
    }

    private getMyleTableColumnsDefaultOptions(): MyleTableColumnOptions[] {
        const mainColumns = [
            new MyleTableColumnOptions({
                HeaderText: cacheStore.GetString("PatientLabel"),
                FieldName: "APSSPatient", // nom + prenom + DDN
                WidthPercentageDefault: 10,
                RowClickEnabled: false,
                IsSortable: false,
                SortColumn: PortfolioExamRequestSortOrderType.Patient,
                SortDirection: SortDirection.ASC
            }),
            new MyleTableColumnOptions({
                HeaderText: cacheStore.GetString("Id"),
                FieldName: "ReqReg_Id",
                WidthPercentageDefault: 8,
                IsHidden: true,
                RowClickEnabled: true
            }),
            new MyleTableColumnOptions({
                HeaderText: cacheStore.GetString("TypeLabel"),
                FieldName: "nomFormulaireLongue",
                WidthPercentageDefault: 12,
                RowClickEnabled: true
            }),
            new MyleTableColumnOptions({
                HeaderText: cacheStore.GetString("CreatedOnDLabel"),
                FieldName: "ReqReg_DateCreation",
                WidthPercentageDefault: 8,
                RowClickEnabled: true,
                IsSortable: false
            }),
            new MyleTableColumnOptions({
                HeaderText: cacheStore.GetString("DateSentLabel"),
                FieldName: "dateTransmissionCRDS",
                WidthPercentageDefault: 8,
                RowClickEnabled: true
            }),
            new MyleTableColumnOptions({
                HeaderText: sessionStore.getDefaultHealthCardLabel(),
                FieldName: "NAM",
                WidthPercentageDefault: 8,
                IsHidden: true,
                RowClickEnabled: true
            }),
            new MyleTableColumnOptions({
                HeaderText: cacheStore.GetString("CreatedByLabel"),
                FieldName: "ReqReg_NomCreateur",
                IsHidden: true,
                WidthPercentageDefault: 8,
                RowClickEnabled: true
            }),
            new MyleTableColumnOptions({
                HeaderText: cacheStore.GetString("MDReferantLabel"),
                FieldName: "nomRef", // + prenomRef
                WidthPercentageDefault: 8,
                RowClickEnabled: true
            }),
            new MyleTableColumnOptions({
                HeaderText: cacheStore.GetString("StatusLabel"),
                FieldName: "etat_texte",
                WidthPercentageDefault: 12,
                RowClickEnabled: true
            }),
            new MyleTableColumnOptions({
                HeaderText: cacheStore.GetString("SubStateLabel"),
                FieldName: "sousEtat_texte",
                WidthPercentageDefault: 12,
                RowClickEnabled: true
            }),
            new MyleTableColumnOptions({
                HeaderText: cacheStore.GetString("LastUpdateLabel"),
                FieldName: "ReqReg_DateChangementEtat",
                WidthPercentageDefault: 8,
                RowClickEnabled: true
            }),
            new MyleTableColumnOptions({
                HeaderText: cacheStore.GetString("NotesLabel"),
                FieldName: "Note",
                WidthPercentageDefault: 14,
                RowClickEnabled: true
            })
        ];

        mainColumns.push(
            new MyleTableColumnOptions({
                HeaderText: cacheStore.GetString("AttachmentsLabel"),
                FieldName: "lstPrerequis", // lstPrerequisManquant (IsGreen / IsRed)
                WidthPercentageDefault: 12,
                RowClickEnabled: false
            })
        );

        return mainColumns;
    }

    private getTableColumns(columnsOptions: Immutable.List<MyleTableColumnOptions>): Immutable.List<MyleTableColumn> {
        return MyleTableColumn.buildList<MyAPSSFormTableItem>(
            columnsOptions,
            column => {
                switch (column.FieldName) {
                    case "APSSPatient":
                        return {
                            renderCell: item => {
                                if (item.PatientLink != null)
                                    return (<div><a onClick={this.openPatient.bind(this, item.PatientLink.P, item.PatientLink.E)}>{item.Model.prenom + " " + item.Model.nom}</a></div>);
                                else
                                    return (<div><SpanControl IsRed={item.IsRedActionRequired()} IsBold={item.IsBold()} text={item.Model.prenom + " " + item.Model.nom} /></div>);
                            }
                        };

                    case "ReqReg_Id":
                        return {
                            renderCell: item => (
                                <div><SpanControl IsRed={item.IsRedActionRequired()} IsBold={item.IsBold()} text={item.Model.ReqReg_Id} /></div>
                            )
                        };

                    case "NAM":
                        return {
                            renderCell: item => (
                                <div><SpanControl IsRed={item.IsRedActionRequired()} IsBold={item.IsBold()} text={item.Model.NAM} /></div>
                            )
                        };

                    case "nomFormulaireLongue":
                        return {
                            renderCell: item => {
                                let displayName = IsNullOrWhiteSpace(item.Model.nomFormulaireLongue) ? "Conseil Numerique" : item.Model.nomFormulaireLongue?.replace("Demande de consultation en", "");

                                return (
                                    <div><SpanControl IsRed={item.IsRedActionRequired()} IsBold={item.IsBold()} text={displayName} /></div>
                                );
                            }
                        };

                    case "ReqReg_DateCreation":
                        return {
                            renderCell: item => {
                                let dateString = "";
                                if (item.Model.ReqReg_DateModification != null) {
                                    const tmpDate: Date = new Date(item.Model.ReqReg_DateCreation);
                                    dateString = tmpDate.toMoment().format("YYYY-MM-DD HH:mm");
                                }
                                return (
                                    <div><SpanControl IsRed={item.IsRedActionRequired()} IsBold={item.IsBold()} text={dateString} /></div>
                                );
                            }
                        };

                    case "dateTransmissionCRDS":
                        return {
                            renderCell: item => {
                                let dateString = "";
                                if (item.Model.ReqReg_DateModification != null) {
                                    const tmpDate: Date = new Date(item.Model.ReqReg_DateModification);
                                    dateString = tmpDate.toMoment().format("YYYY-MM-DD HH:mm");
                                }

                                return (
                                    <div><SpanControl IsRed={item.IsRedActionRequired()} IsBold={item.IsBold()} text={dateString} /></div>
                                );
                            }
                        };

                    case "etat_texte":
                        return {
                            renderCell: item => (
                                <div>
                                    <SpanControl
                                        IsRed={item.IsRedActionRequired()}
                                        IsBold={item.IsBold()}
                                        text={`${item.Model.etat_texte}`}
                                        title={`${item.Model.etat_texte}`}
                                    />
                                </div>
                            )
                        };

                    case "sousEtat_texte":
                        return {
                            renderCell: item => (
                                <div>
                                    <SpanControl
                                        IsRed={item.IsRedActionRequired()}
                                        IsBold={item.IsBold()}
                                        text={`${item.Model.sousEtat_texte}`}
                                        title={`${item.Model.sousEtat_texte}`}
                                    />
                                </div>
                            )
                        };

                    case "ReqReg_NomCreateur":
                        return {
                            renderCell: item => (
                                <div><SpanControl IsRed={item.IsRedActionRequired()} IsBold={item.IsBold()} text={item.Model.ReqReg_NomCreateur} /></div>
                            )
                        };

                    case "nomRef":
                        return {
                            renderCell: item => (
                                <div><SpanControl IsRed={item.IsRedActionRequired()} IsBold={item.IsBold()} text={item.Model.prenomRef + " " + item.Model.nomRef} /></div>
                            )
                        };

                    case "ReqReg_DateChangementEtat":
                        return {
                            renderCell: item => {
                                let dateString = item.Model.ReqReg_DateChangementEtat
                                    ? new Date(item.Model.ReqReg_DateChangementEtat).toMoment().format("YYYY-MM-DD HH:mm")
                                    : "";

                                return (
                                    <div><SpanControl IsRed={item.IsRedActionRequired()} IsBold={item.IsBold()} text={dateString} /></div>
                                );
                            }
                        };

                    case "Note":
                        return {
                            renderCell: item => (
                                <div><SpanControl
                                    IsRed={item.IsRedActionRequired()}
                                    IsBold={item.IsBold()}
                                    text={item.Model.saFHIR_infoSupplementaireCRDS ?? ""}
                                    title={item.Model.saFHIR_infoSupplementaireCRDS ?? ""}
                                />
                                </div>
                            )
                        };

                    case "Actions":
                        return {
                            renderCell: item => {
                                const isArchived = item.Model.dmeReserve_archiveFlag == "1";
                                return (
                                    <div>
                                        {!isArchived &&
                                            (
                                                <div>
                                                    <a
                                                        key={"action_archive_" + item.Model.ReqReg_Id}
                                                        onClick={async () => this.ArchiveCrdsPurgedRequest(item.Model.ReqReg_Id, item.Model.ReqReg_EtablissementCreateur)}
                                                    >
                                                        {cacheStore.GetString("ArchiveLabel")}
                                                    </a>
                                                </div>
                                            )
                                        }
                                        {isArchived &&
                                            (
                                                <div>
                                                    <a
                                                        key={"action_unarchive_" + item.Model.ReqReg_Id}
                                                        onClick={async () => this.RestoreCrdsPurgedRequest(item.Model.ReqReg_Id, item.Model.ReqReg_EtablissementCreateur)}
                                                    >
                                                        {cacheStore.GetString("RestoreLabel")}
                                                    </a>
                                                </div>
                                            )
                                        }
                                    </div>
                                );
                            }
                        };

                    case "lstPrerequis":
                        return {
                            renderCell: item => {
                                if (item.ExpandPrerequis == true || item.Prerequis.length == 0) {
                                    return (
                                        <div>
                                            {item.Prerequis.map((x, idx) => {
                                                if (x.IsMissing == true) {
                                                    return (
                                                        <div key={item.Model.ReqReg_Id + "_prer_" + idx} className="flex-container" style={{ justifyContent: "flex-start" }}>
                                                            <div><a onClick={this.openAttachPopup.bind(this, item.Model.ReqReg_Id, x.Code, item)}>{cacheStore.GetString("AttachLabel")}</a></div>
                                                            <div style={{ marginLeft: "8px" }}><SpanControl IsRed={true} text={x.Name} /></div>
                                                        </div>
                                                    );
                                                }
                                                else
                                                    return <div key={item.Model.ReqReg_Id + "_prer_" + idx} onClick={ApssInbox.openAPSSDocument.bind(this, item.Model.ReqReg_Id, x.Code)}><a style={{ color: "#1e355c" }}><SpanControl IsRed={item.IsRedActionRequired()} text={x.Name} /></a></div>;
                                            })}

                                            <div key={item.Model.ReqReg_Id + "_prer_" + "autre"} className="flex-container" style={{ justifyContent: "flex-start" }}>
                                                <div><a onClick={this.openAttachPopup.bind(this, item.Model.ReqReg_Id, "rprt_AUTRES", item)}>{cacheStore.GetString("AttachLabel")}</a></div>
                                                <div style={{ marginLeft: "8px" }}>
                                                    <div onClick={ApssInbox.openAPSSDocument.bind(this, item.Model.ReqReg_Id, "rprt_AUTRES")}>
                                                        <a style={{ color: "#1e355c" }}><SpanControl IsRed={item.IsRedActionRequired()} text={cacheStore.GetString("OtherReportLabel")} /></a>
                                                    </div>
                                                </div>
                                            </div>
                                        </div>
                                    );
                                }
                                else {
                                    const documentsDisplay = item.Prerequis.length > 0
                                        ? "(" + (item.Prerequis.length - item.PrerequisManquantExistingCount) + "/" + item.Prerequis.length + ")"
                                        : "";

                                    const isRed = item.PrerequisManquantExistingCount > 0;

                                    return (<div><a style={{ color: "#1e355c" }} onClick={this.expandAPSSTableItem.bind(this, item.Model.ReqReg_Id)}><SpanControl IsRed={isRed} text={documentsDisplay} /></a></div>);
                                }
                            }
                        };

                    default:
                        return {
                            renderCell: () => (
                                <div>TODO</div>
                            )
                        };
                }
            },
            false
        );
    }

    private async ArchiveCrdsPurgedRequest(apssFormId: string, historiqueId: string): Promise<void> {
        await ApssClient.ArchiveRequest(apssFormId, historiqueId);
        sessionStore.dispatch(LocalOperations.Portfolio.APSS_Reload, { inboxType: this.props.Type });
    }
    private async RestoreCrdsPurgedRequest(apssFormId: string, historiqueId: string): Promise<void> {
        await ApssClient.RestoreRequest(apssFormId, historiqueId);
        sessionStore.dispatch(LocalOperations.Portfolio.APSS_Reload, { inboxType: this.props.Type });
    }

    private loadTableRenderOptions(columnOptions: Immutable.List<MyleTableColumnOptions>): ITableRenderOptions {
        // Get The Render Options
        return Immutable.fromJS({
            MinColWidth: 60,
            MinTableWidth: 980,
            Cols: columnOptions.filter(x => x.IsHidden === false).map(i => i.WidthPercentage),
            FirstColumnNoDrag: false,
            LastColumnNoDrag: true,
            Autoresize: true,
            OnColumnWidthChange: null
        });
    }

    private openPatient(patientContactId: string, NoteId: string): void {
        window.setTimeout(() => {
            const openPatientOperation = new LocalEvent(LocalOperations.Misc.RedirectToTab);

            openPatientOperation.AddItem("value", patientContactId);
            openPatientOperation.AddItem("target", RedirectTo.Patient);
            openPatientOperation.AddItem("currentLocation", RouterHistory.location);
            openPatientOperation.AddItem("OpenEncounterId", NoteId);
            openPatientOperation.AddItem("encounterEdit", false);

            LocalEventDispatcher.dispatch(openPatientOperation);
        }, 100);
    }

    private expandAPSSTableItem(id: string): void {
        const data = portfolioStore.apssInboxModel.get("TableData").toArray();
        if (data !== null) {
            for (const item of data) {
                item.ExpandPrerequis = false;

                if (item.Model.ReqReg_Id == id)
                    item.ExpandPrerequis = true;
            }
        }

        const operation = new LocalEvent(LocalOperations.Portfolio.APSS_ExpandTableItem);
        operation.AddItem("id", id);
        operation.AddItem("data", data.toImmutable());
        LocalEventDispatcher.dispatch(operation);
    }

    private saveAndCloseSettingPopup(): void {
        if (portfolioStore.apssInboxModel.get("ShowSettings") == true)
            sessionStore.dispatch(LocalOperations.Portfolio.APSS_HideSettings);
    }

    private openAttachPopup(requestId: string, documentCode: string, item: MyAPSSFormTableItem): void {
        const requestData = {
            AttachmentPopupResponseCallback: (attachments: ExportableFile[]) => portfolioStore.PortfolioActions.AttachAPSSFiles(requestId, documentCode, attachments),
            AttachmentPopupMaxFiles: 5,
            Model: this.props.Model
        };

        if (item.PatientLink == null)
            requestData["AttachmentPopupPatientSearch"] = item.Model.nom + " " + item.Model.prenom + " " + new Date(item.Model.DDN).getFullYear().toString();
        else
            requestData["AttachmentPopupPatientPerson"] = new Person({
                ContactId: item.PatientLink.P,
                FirstName: item.Model.prenom,
                LastName: item.Model.nom,
                NAM: item.Model.NAM
            });

        sessionStore.dispatchMain(LocalOperations.MainComponentViewOperations.DisplayAttachmentPopup, requestData);
    }

    public static async openAPSSDocument(requeteId: string, DocumentCode: string, e: React.MouseEvent): Promise<void> {
        const eventX = e.clientX.toString();
        const eventY = e.clientY.toString();

        const response = await ApssClient.post("/Requetes/api/rest/v1/requetes/" + requeteId);
        const attachments: any[] = response.fichiersJoints?.[DocumentCode] ?? [];

        switch (attachments.length) {
            case 0:
                console.error(`The CRDS API did not return any attachments for document ID ${requeteId}.`);
                break;

            case 1:
                ApssClient.openFormFromUrl(attachments[0].url, null, true);
                break;

            default:
                sessionStore.dispatchMain(
                    LocalOperations.MainComponentViewOperations.DisplayOpenAttachmentPopup,
                    {
                        Attachments: attachments.map(p => new OpenAttachmentsPopupItem({
                            fileTitle: p.title,
                            openFileCallback: () => ApssClient.openFormFromUrl(p.url, null, true)
                        })),
                        Xpos: Number(eventX),
                        Ypos: Number(eventY)
                    }
                );
                break;
        }
    }
}

export { ApssInbox };
